import * as React from 'react';

import {colors} from '../colors/colors';
import {ErrorMessage} from '../form/error-message';

// @ts-expect-error - imported from .js/.jsx file
import {LoadingIcon} from '../icon/svg/loading-icon';
import {BACKSPACE, LEFT, RIGHT, TAB} from '../utils/keys';

import styles from './mfa-code.module.css';

type Props = {
    inputCount: number;
    onVerifyMfaCode: (arg1: string) => Promise<any>;
    isMobile: boolean;
};

export function MfaCode(props: Props) {
    const inputRefs = React.useRef([]);
    const inputBoxes = [];

    const [isLoading, setIsLoading] = React.useState(false);
    const [isError, setIsError] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState(
        'This code doesn’t look quite right — try retyping it'
    );

    // @ts-expect-error
    const handleKeyDown = (event) => {
        setIsError(false);
        if (isBackspaceEvent(event)) {
            if (isEmptyInput(event)) {
                deleteInput(event, true); // deletePreviousInput
                focusInput(event, false, true); // focusPreviousInput
            } else {
                // Do nothing, onChange will delete the input
            }
        } else if (isRightArrowEvent(event)) {
            focusInput(event, true); // focusNextInput
        } else if (isLeftArrowEvent(event)) {
            focusInput(event, true, true); // focusPreviousInput
        } else if (isShiftTabEvent(event)) {
            event.preventDefault();
            focusInput(event, true, true); // focusPreviousInput
        } else if (isTabEvent(event)) {
            event.preventDefault();
            focusInput(event); // focusNextInput
        } else if (!isNumeric(event) && event.keyCode !== 91 && event.keyCode !== 86) {
            // If not numerical input, or control or v (for paste), ignore it.
            event.preventDefault();
        } else if (!isEmptyInput(event)) {
            // If the selected box already has a character, overwrite it
            deleteInput(event); // deleteCurrentInput
        }
    };

    // @ts-expect-error
    const handleChange = (event) => {
        if (!isEmptyInput(event)) {
            focusInput(event); // focusNextInput
        }

        verifyCode(event.target.value);
    };

    // @ts-expect-error
    const handleClick = (event) => {
        const tabIndex = event.currentTarget.tabIndex;
        const input = inputRefs.current[tabIndex];

        // Timeout ensures that select state sticks
        setTimeout(function () {
            // @ts-expect-error
            if (input instanceof HTMLInputElement) {
                // @ts-expect-error
                input.select();
            }
        }, 0);
        // @ts-expect-error
        if (input instanceof HTMLInputElement) {
            // @ts-expect-error
            input.placeholder = '';
        }
    };

    /**
     * Override default paste behavior, insert each char into boxes sequentially
     * @param {*} event Paste event
     */
    // @ts-expect-error
    const handlePaste = (event) => {
        event.preventDefault();

        // Get pasted text
        let pastedText;
        // @ts-expect-error
        if (window.clipboardData && window.clipboardData.getData) {
            // Legacy support
            // @ts-expect-error
            pastedText = window.clipboardData.getData('Text');
        } else if (event.clipboardData && event.clipboardData.getData) {
            pastedText = event.clipboardData.getData('text/plain');
        }

        // Remove any spaces from pasted text, Set text in boxes to the text that was pasted
        if (pastedText !== undefined) {
            pastedText = pastedText.split(' ').join('');
            setValue(pastedText);
            focusGivenInput(Math.min(pastedText.length, props.inputCount - 1));
        }

        verifyCode(event.target.value);
    };

    // @ts-expect-error
    const focusInput = (event, select = false, previous = false) => {
        // Focus the next or previous input, determined by boolean
        const tabIndex = previous
            ? event.currentTarget.tabIndex - 1
            : event.currentTarget.tabIndex + 1;

        focusGivenInput(tabIndex, select);
    };

    // @ts-expect-error
    const focusGivenInput = (tabIndex, select = false) => {
        const input = inputRefs.current[tabIndex];

        // @ts-expect-error
        if (input instanceof HTMLInputElement) {
            if (select) {
                // Timeout ensures that select state sticks
                setTimeout(function () {
                    // @ts-expect-error
                    input.select();
                }, 0);
            }

            // @ts-expect-error
            input.placeholder = '';
            // @ts-expect-error
            input.focus();
        }
    };

    // @ts-expect-error
    const handleBlur = (event) => {
        // Restore placeholder when input loses focus
        const tabIndex = event.currentTarget.tabIndex;
        const input = inputRefs.current[tabIndex];

        // @ts-expect-error
        if (input instanceof HTMLInputElement) {
            // @ts-expect-error
            input.placeholder = '-';
        }
    };

    // @ts-expect-error
    const deleteInput = (event, previous = false) => {
        const tabIndex = previous ? event.currentTarget.tabIndex - 1 : event.currentTarget.tabIndex;
        const input = inputRefs.current[tabIndex];

        // @ts-expect-error
        if (input instanceof HTMLInputElement) {
            // @ts-expect-error
            input.value = '';
        }
    };

    // @ts-expect-error
    const isEmptyInput = (event) => {
        const tabIndex = event.currentTarget.tabIndex;
        const input = inputRefs.current[tabIndex];

        // @ts-expect-error
        if (input instanceof HTMLInputElement) {
            // @ts-expect-error
            if (input.value === '') {
                return true;
            } else {
                return false;
            }
        }
    };

    // @ts-expect-error
    const isShiftTabEvent = (event) => {
        if (event.shiftKey) {
            if (event.keyCode === TAB) {
                return true;
            } else {
                return false;
            }
        }

        return false;
    };

    // @ts-expect-error
    const isNumeric = (event) => {
        return event.keyCode >= 48 && event.keyCode <= 57;
    };

    // @ts-expect-error
    const isBackspaceEvent = (event) => {
        return event.keyCode === BACKSPACE;
    };

    // @ts-expect-error
    const isLeftArrowEvent = (event) => {
        return event.keyCode === LEFT;
    };

    // @ts-expect-error
    const isRightArrowEvent = (event) => {
        return event.keyCode === RIGHT;
    };

    // @ts-expect-error
    const isTabEvent = (event) => {
        return event.keyCode === TAB;
    };

    /**
     * Set the value of text boxes
     * @param {*} value Value to be set
     */
    // @ts-expect-error
    const setValue = (value) => {
        for (let i = 0; i < props.inputCount; i++) {
            const input = inputRefs.current[i];
            // @ts-expect-error
            if (input instanceof HTMLInputElement) {
                // @ts-expect-error
                input.value = value.charAt(i);
            }
        }
    };

    /**
     * Get the value held acrossed the text boxes
     * @returns User entered value
     */
    const getValue = () => {
        let value = '';

        for (let i = 0; i < props.inputCount; i++) {
            const input = inputRefs.current[i];
            // @ts-expect-error
            if (input instanceof HTMLInputElement) {
                // @ts-expect-error
                value = value.concat(input.value);
            }
        }

        return value;
    };

    const disableInputs = (disable: boolean) => {
        for (let i = 0; i < props.inputCount; i++) {
            const input = inputRefs.current[i];
            // @ts-expect-error
            if (input instanceof HTMLInputElement) {
                // @ts-expect-error
                input.disabled = disable;
            }
        }
    };

    /** Clear and re-enable input, end loading indicator, focus first box */
    const reset = () => {
        setIsLoading(false);
        disableInputs(false);
        setValue('');
        focusGivenInput(0, true);
    };

    // @ts-expect-error
    const verifyCode = (value) => {
        let code = value;
        if (!props.isMobile) {
            code = getValue();
        }

        if (code.length === props.inputCount) {
            setIsLoading(true);
            disableInputs(true);

            props
                .onVerifyMfaCode(code)
                .then(() => {
                    // no-op, handled by parent
                })
                .catch((result) => {
                    setIsError(true);

                    if (result && result.messages.length && result.messages[0]) {
                        setErrorMessage(result.messages[0]);
                    } else {
                        setErrorMessage('Something went wrong');
                    }

                    reset();
                });
        }
    };

    // Generate input box for each digit
    for (let i = 0; i < props.inputCount; i++) {
        inputBoxes.push(
            <input
                key={i}
                // @ts-expect-error
                ref={(el) => (inputRefs.current[i] = el)}
                // @ts-expect-error
                id={i}
                autoComplete='off'
                onKeyDown={handleKeyDown}
                onChange={handleChange}
                onClick={handleClick}
                onBlur={handleBlur}
                // @ts-expect-error
                maxLength='1'
                placeholder={'-'}
                tabIndex={i}
                onPaste={handlePaste}
                className={styles['tfa-code']}
            />
        );
    }

    const mobileInputBox = (
        <input
            autoComplete='off'
            onKeyDown={handleKeyDown}
            onChange={handleChange}
            onClick={handleClick}
            maxLength={props.inputCount}
            placeholder='- - - - - -'
            tabIndex={0}
            className={styles['tfa-code-mobile']}
        />
    );

    return (
        <>
            <div className={styles['mfa-row']}>
                {props.isMobile ? mobileInputBox : inputBoxes}
                {isLoading ? (
                    <LoadingIcon
                        fill={colors.offWhiteDk}
                        size={40}
                        className={styles['loadingIcon']}
                    />
                ) : undefined}
            </div>
            {isError ? <ErrorMessage>{errorMessage}</ErrorMessage> : undefined}
        </>
    );
}
